import React, { useContext } from 'react';
import Helmet from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { Button } from 'localmed-core';

import { widgetSettingsShape } from '../../../shared/components/WidgetSettings';
import {
  SchedulingSessionStateContext,
  createSchedulingSessionStateLandingPage,
} from '../../../shared/components/SchedulingSessionState';
import { WIDGET_CHROME } from '../../../shared/constants';
import { historyShape, locationShape } from '../../../shared/shapes';
import { buildOfficeSchedulingUrl } from '../../../shared/utils/urls';
import WidgetLayout from '../../components/WidgetLayout';
import WidgetOfficeHeader from '../../components/WidgetOfficeHeader';
import WidgetRatings from '../../components/WidgetRatings';
import NotFoundPage from '../NotFoundPage';
import LoadingPage from '../LoadingPage';
import OfficeReview from './OfficeReview';

import style from './OfficeReviewsPage.scss';

const OfficeReviewsPage = ({ data: { loading, office }, history, location }) => {
  const {
    widgetSettings: { chrome },
  } = useContext(SchedulingSessionStateContext);
  if (loading) {
    return <LoadingPage />;
  }
  if (!office) {
    return <NotFoundPage />;
  }
  const officeSchedulingUrl = buildOfficeSchedulingUrl(office.id, { location });
  if (!office.reviewsEnabled) {
    history.replace(officeSchedulingUrl);
  }
  return (
    <WidgetLayout
      header={<WidgetOfficeHeader office={office} />}
      callToAction={
        <Button tier="alert" full to={officeSchedulingUrl}>
          <FormattedMessage
            id="widgets.OfficeReviewsPage.callToAction"
            defaultMessage="Schedule Online"
          />
        </Button>
      }
      isEmbedded={chrome === WIDGET_CHROME.EMBEDDED}
    >
      <Helmet>
        <title>{office.practice.display}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <WidgetRatings
        ratings={[
          office.localmedRating,
          office.googleRating,
          office.facebookRating,
          office.yelpRating,
        ]}
      />
      <ol className={style['office-reviews-page__review-list']}>
        {office.partnerReviews.nodes.map(review => (
          <li key={review.id} className={style['office-reviews-page__review']}>
            <OfficeReview review={review} />
          </li>
        ))}
      </ol>
    </WidgetLayout>
  );
};

OfficeReviewsPage.propTypes = {
  history: historyShape,
  location: locationShape,
  data: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    office: PropTypes.shape({
      id: PropTypes.string.isRequired,
      practice: PropTypes.shape({
        display: PropTypes.string.isRequired,
      }).isRequired,
      reviewsEnabled: PropTypes.bool,
      localmedRating: PropTypes.object,
      facebookRating: PropTypes.object,
      googleRating: PropTypes.object,
      yelpRating: PropTypes.object,
      partnerReviews: PropTypes.shape({
        nodes: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
          })
        ).isRequired,
      }).isRequired,
    }),
  }).isRequired,
  widgetSettings: widgetSettingsShape,
};

export default createSchedulingSessionStateLandingPage(OfficeReviewsPage);
